import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import styles from './toast-content.module.scss';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {FeedbackReaction, FeedbackState} from '../feedback-toast.types';

export interface ToastContentProps {
  setShowFeedbackToast: (showFeedbackToast: boolean) => void;
  onFeedbackReaction: (reaction: FeedbackReaction) => Promise<void>;
  onFeedbackTextInput: (feedback: string) => Promise<void>;
  reactionPageHeaderTitle: string;
  reactionPageHeaderIcon: string;
  feedbackInputHeaderTitle: string;
  feedbackInputHeaderIcon: string;
  feedbackInputPlaceholder: string;
  postSubmissionText: string;
}

export function ToastContent({setShowFeedbackToast, ...props}: ToastContentProps): ReactElement {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(FeedbackState.REACTION_INPUT);
  const [textInputContent, setTextInputContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReaction, setSelectedReaction] = useState<FeedbackReaction>();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const FEEDBACK_AUTO_CLOSE_AFTER_SUBMISSION_TIMEOUT_MS = 3000;
  const FEEDBACK_COMMENT_MAX_LENGTH = 200;

  useEffect(() => {
    if (feedbackState === FeedbackState.TEXT_INPUT) {
      textAreaRef.current?.focus();
    }

    if (feedbackState === FeedbackState.SUBMITTED) {
      setTimeout(() => {
        setShowFeedbackToast(false);
      }, FEEDBACK_AUTO_CLOSE_AFTER_SUBMISSION_TIMEOUT_MS);
    }
  }, [feedbackState]);

  const getHeaderContent = (): ReactElement => {
    const reactionInputState = feedbackState === FeedbackState.REACTION_INPUT;
    return (
      <div className="flex-row-align-center flex-row-justify-between ">
        <div className="flex-row-align-center gap-2">
          <Icon
            icon={reactionInputState ? props.reactionPageHeaderIcon : props.feedbackInputHeaderIcon}
            size={IconSize.SIZE_ICON_16}
            className={`${reactionInputState ? 'content-success' : ''} spacing-p-l-1`}
          />
          <Text
            val={reactionInputState ? props.reactionPageHeaderTitle : props.feedbackInputHeaderTitle}
            size={TextSize.XSMALL}
            className={reactionInputState ? 'content-success' : ''}
          />
        </div>
        <Button
          customClasses="spacing-p-2"
          size={Size.XSMALL}
          icon="icon-close"
          type={Type.GHOST}
          onClick={() => {
            setShowFeedbackToast(false);
          }}
        />
      </div>
    );
  };

  const reactionClickHandler = async (reaction: FeedbackReaction):Promise<void> => {
    setSelectedReaction(reaction);
    setIsLoading(true);
    await props.onFeedbackReaction(reaction);
    setIsLoading(false);
    setFeedbackState(reaction === FeedbackReaction.POSITIVE ? FeedbackState.SUBMITTED : FeedbackState.TEXT_INPUT);
  };

  const positiveReactionClickHandler = async ():Promise<void> => {
    await reactionClickHandler(FeedbackReaction.POSITIVE);
  };

  const negativeReactionClickHandler = async ():Promise<void> => {
    await reactionClickHandler(FeedbackReaction.NEGATIVE);
  }

  const getReactionButton = (reaction: FeedbackReaction, icon: string, onClick: () => Promise<void>): ReactElement => {
    return (
      <Button
        customClasses={`${styles.feedbackButton} spacing-p-2`}
        size={Size.XSMALL}
        icon={icon}
        type={Type.GHOST}
        onClick={onClick}
        isLoading={isLoading && selectedReaction === reaction}
      />
    );
  };

  const getReactionButtons = (): ReactElement => {
    return (
      <div className="flex-row-align-center flex-row-justify-between">
        <Text val={'Happy with the results?'} size={TextSize.XSMALL} className=" spacing-p-l-2" />
        <div className="flexbox gap-2">
          {getReactionButton(FeedbackReaction.POSITIVE, "icon-thumbs-up", positiveReactionClickHandler)}
          {getReactionButton(FeedbackReaction.NEGATIVE, "icon-thumbs-down", negativeReactionClickHandler)}
        </div>
      </div>
    );
  };

  const getTextInput = (): ReactElement => {
    return (
      <div className={styles.textInputContainer}>
        <textarea
          ref={textAreaRef}
          className={styles.textArea}
          placeholder={props.feedbackInputPlaceholder}
          value={textInputContent}
          onChange={(e) => {
            const input = e.target.value;
            setTextInputContent(input);
          }}
          maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
        />
        <Button
          customClasses={`${styles.submitButton} spacing-p-2`}
          size={Size.XSMALL}
          icon="icon-send"
          type={Type.GHOST}
          disabled={!textInputContent}
          onClick={async () => {
            setIsLoading(true);
            await props.onFeedbackTextInput(textInputContent);
            setFeedbackState(FeedbackState.SUBMITTED);
            setIsLoading(false);
          }}
          isLoading={isLoading}
        />
      </div>
    );
  };

  const getSubmittedMessage = (): ReactElement => {
    return <Text val={props.postSubmissionText} size={TextSize.XXSMALL} className={`${styles.submittedMessage} bg-success-10 content-success`} />;
  };

  const getFooterContent = (): ReactElement => {
    switch (feedbackState) {
      case FeedbackState.REACTION_INPUT:
        return getReactionButtons();
      case FeedbackState.TEXT_INPUT:
        return getTextInput();
      case FeedbackState.SUBMITTED:
        return getSubmittedMessage();
    }
  };

  const getContent = (): ReactElement => {
    return (
      <div className="flexbox flex-column gap-2 _full-width">
        {getHeaderContent()}
        {getFooterContent()}
      </div>
    );
  };

  return <div className={styles.contentContainer}>{getContent()}</div>;
}
